import { CHARACTER_ACTIONS, type CharacterActionKey } from "@/character/actions";
import {
  defaultGridTuning,
  gridTuningForImageSize,
  type GridTuning,
} from "@/character/draft";
import { readImageFileAsDataUrl } from "@/character/frame";
import {
  GRID_COLUMNS,
  GRID_ROWS,
  sheetLayoutFromImage,
  supportsTemplateGridLayout,
  TEMPLATE_SHEET_HEIGHT,
  TEMPLATE_SHEET_WIDTH,
} from "@/character/grid";
import { parseCharacterSpritesheet } from "@/character/spritesheet";
import {
  applySheetUploadClean,
  bindSheetUploadCleanControls,
  getSheetUploadCleanPrefs,
  readSheetUploadCleanPrefsFromHost,
  renderSheetUploadCleanControlsHtml,
} from "@/character/sheet-upload-clean";
import { mountSheetUploadPreview } from "@/character/sheet-upload-preview";
import { downloadCharacterTemplate, downloadMotionTemplate } from "@/character/template";
import {
  createGmMonsterTemplate,
  defaultNewMonsterForm,
  deleteGmMonsterFromCatalog,
  GM_MONSTER_PLACEHOLDER_IMAGE,
  gmMonsterTemplateToPart,
  hydrateGmMonsterCatalog,
  loadGmMonsterCatalog,
  type GmMonsterTemplate,
  isStorageQuotaError,
  upsertGmMonsterCatalog,
} from "@/gm/gm-monster-catalog";
import { loadHomeRoom, saveHomeRoom } from "@/home/storage";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

type FormState = Omit<GmMonsterTemplate, "version" | "createdAt" | "updatedAt"> & {
  createdAt?: number;
};

let draftSheetUrl: string | null = null;
let draftSheetSourceUrl: string | null = null;
let draftGridTuning: GridTuning = defaultGridTuning();
let editingId: string | null = null;

function formFromDefaults(): FormState {
  const d = defaultNewMonsterForm();
  draftSheetUrl = null;
  draftSheetSourceUrl = null;
  draftGridTuning = defaultGridTuning();
  editingId = null;
  return { ...d, id: "" };
}

let formState: FormState = formFromDefaults();

function previewSheetUrl(): string | null {
  return (
    draftSheetUrl ??
    formState.sourceSheetDataUrl ??
    formState.spritesheetDataUrl ??
    null
  );
}

function renderCardGrid(): string {
  const items = loadGmMonsterCatalog();
  if (items.length === 0) {
    return `
      <div class="gm-monster-empty">
        <p>ยังไม่มีมอนสเตอร์</p>
        <p class="hint">กด <strong>+</strong> เพื่อสร้างแม่แบบใหม่</p>
      </div>
    `;
  }
  return `
    <div class="gm-monster-card-grid" role="list">
      ${items
        .map(
          (m) => `
        <article class="gm-monster-card" role="listitem" data-monster-id="${escapeHtml(m.id)}">
          <div class="gm-monster-card-art-wrap">
            <img class="gm-monster-card-art" src="${escapeHtml(m.imageDataUrl)}" alt="" width="72" height="72" loading="lazy" />
            ${m.spritesheetDataUrl ? `<span class="gm-monster-card-badge">sheet</span>` : ""}
          </div>
          <div class="gm-monster-card-body">
            <h3 class="gm-monster-card-title">${escapeHtml(m.name)}</h3>
            <p class="gm-monster-card-stats hint">Lv.${m.monsterLevel} · HP ${m.monsterBaseHp.toLocaleString("en-US")} · ATK ${m.monsterBaseAttack}</p>
            <p class="gm-monster-card-stats hint">EXP ${m.monsterXpReward.toLocaleString("en-US")} · ${m.cellsW}×${m.cellsH} ช่อง</p>
          </div>
          <footer class="gm-monster-card-actions">
            <button type="button" class="btn btn-ghost btn-xs gm-monster-edit" data-id="${escapeHtml(m.id)}">แก้ไข</button>
            <button type="button" class="btn btn-ghost btn-xs gm-monster-home" data-id="${escapeHtml(m.id)}">คลังบ้าน</button>
            <button type="button" class="btn btn-ghost btn-xs gm-monster-delete" data-id="${escapeHtml(m.id)}">ลบ</button>
          </footer>
        </article>`,
        )
        .join("")}
    </div>
  `;
}

function renderForm(): string {
  const f = formState;
  const hasSheet = Boolean(previewSheetUrl());
  const actionList = CHARACTER_ACTIONS
    .map(
      (action) =>
        `<li><strong>${escapeHtml(action.templateLabel)}</strong> — แถว ${action.row + 1}</li>`,
    )
    .join("");
  const previewBlock = hasSheet
    ? `<div id="gm-monster-sheet-preview-host"></div>`
    : `<div class="sheet-preview sheet-preview--empty">ยังไม่ได้อัปโหลดสไปรต์ชีต (idle/run/attack ฯลฯ)</div>`;

  return `
    <form class="gm-monster-form" id="gm-monster-form">
      <section class="gm-monster-form-section" aria-labelledby="gm-monster-sec-sheet">
        <h3 class="gm-monster-form-section-title" id="gm-monster-sec-sheet">สไปรต์ชีต</h3>
        <p class="hint gm-monster-form-lead">${GRID_COLUMNS} เฟรม/แถว · ${GRID_ROWS} แถว · ${TEMPLATE_SHEET_WIDTH}×${TEMPLATE_SHEET_HEIGHT} px</p>
        <div class="gm-monster-template-row">
          <button type="button" class="btn btn-ghost btn-sm" id="gm-monster-download-template">ดาวน์โหลดเทมเพลต</button>
          <details class="gm-monster-form-details">
            <summary>ทีละ motion</summary>
            <div class="gm-monster-motion-chips">
              ${CHARACTER_ACTIONS
                .map(
                  (action) =>
                    `<button type="button" class="btn btn-ghost btn-xs" data-gm-motion="${action.key}">${action.templateLabel}</button>`,
                )
                .join("")}
            </div>
          </details>
        </div>
        <div id="gm-monster-upload-clean" class="gm-monster-clean-host">${renderSheetUploadCleanControlsHtml("gm-monster-clean")}</div>
        <div class="gm-monster-upload-zone">
          <label class="gm-monster-upload-label">
            <span class="gm-monster-upload-icon" aria-hidden="true">↑</span>
            <span class="gm-monster-upload-text">อัปโหลดสไปรต์ชีต</span>
            <span class="hint">PNG / JPG · idle · run · attack</span>
            <input type="file" id="gm-monster-sheet-file" accept="image/png,image/jpeg,image/webp" hidden />
          </label>
          <div class="gm-monster-preview-slot">${previewBlock}</div>
        </div>
        <details class="gm-monster-form-details gm-monster-form-details--muted">
          <summary>แผนที่แถว action</summary>
          <ul class="action-row-list gm-monster-action-map">${actionList}</ul>
        </details>
      </section>

      <section class="gm-monster-form-section" aria-labelledby="gm-monster-sec-info">
        <h3 class="gm-monster-form-section-title" id="gm-monster-sec-info">ข้อมูล</h3>
        <label class="field gm-monster-field">
          <span class="field-label">ชื่อ</span>
          <input type="text" class="input" id="gm-monster-name" value="${escapeHtml(f.name)}" maxlength="80" required />
        </label>
        <div class="gm-monster-grid gm-monster-grid--footprint">
          <label class="field gm-monster-field"><span class="field-label">กว้าง (ช่อง)</span>
            <input type="number" class="input" id="gm-monster-cells-w" min="1" max="4" value="${f.cellsW}" /></label>
          <label class="field gm-monster-field"><span class="field-label">สูง (ช่อง)</span>
            <input type="number" class="input" id="gm-monster-cells-h" min="1" max="4" value="${f.cellsH}" /></label>
        </div>
      </section>

      <section class="gm-monster-form-section" aria-labelledby="gm-monster-sec-stats">
        <h3 class="gm-monster-form-section-title" id="gm-monster-sec-stats">ค่าพลัง</h3>
        <div class="gm-monster-grid gm-monster-grid--stats">
          <label class="field gm-monster-field"><span class="field-label">Lv</span>
            <input type="number" class="input" id="gm-monster-level" min="1" max="100" value="${f.monsterLevel}" /></label>
          <label class="field gm-monster-field"><span class="field-label">HP</span>
            <input type="number" class="input" id="gm-monster-hp" min="1" value="${f.monsterBaseHp}" /></label>
          <label class="field gm-monster-field"><span class="field-label">ATK</span>
            <input type="number" class="input" id="gm-monster-atk" min="1" value="${f.monsterBaseAttack}" /></label>
          <label class="field gm-monster-field"><span class="field-label">DEF</span>
            <input type="number" class="input" id="gm-monster-def" min="0" value="${f.monsterBaseDefense}" /></label>
          <label class="field gm-monster-field"><span class="field-label">หลบ</span>
            <input type="number" class="input" id="gm-monster-dodge" min="0" value="${f.monsterBaseDodge}" /></label>
          <label class="field gm-monster-field"><span class="field-label">EXP</span>
            <input type="number" class="input" id="gm-monster-xp" min="0" value="${f.monsterXpReward}" /></label>
        </div>
      </section>

      <p class="hint gm-monster-form-status" id="gm-monster-status" role="status"></p>
      <div class="gm-monster-modal-foot">
        <button type="button" class="btn btn-ghost" id="gm-monster-cancel-edit">ยกเลิก</button>
        <button type="submit" class="btn btn-primary">${editingId ? "บันทึก" : "สร้างมอนสเตอร์"}</button>
      </div>
    </form>
  `;
}

function modalTitle(): string {
  return editingId ? "แก้ไขมอนสเตอร์" : "สร้างมอนสเตอร์";
}

export function renderGmMonstersSectionHtml(): string {
  const count = loadGmMonsterCatalog().length;
  return `
    <p class="hint gm-settings-lead">
      แม่แบบมอนสเตอร์ GM · sync คลังบ้านเมื่อเปิด <a href="/home">/home</a> หรือกด «คลังบ้าน» บนการ์ด
    </p>
    <div class="gm-monsters-page">
      <header class="gm-monsters-toolbar">
        <div class="gm-monsters-toolbar-text">
          <h2 class="gm-monsters-toolbar-title">มอนสเตอร์ในแคตตาล็อก</h2>
          <p class="hint" id="gm-monster-catalog-count">${count} รายการ</p>
        </div>
        <button type="button" class="gm-monster-fab" id="gm-monster-open-create" aria-label="สร้างมอนสเตอร์">
          <span aria-hidden="true">+</span>
        </button>
      </header>
      <div id="gm-monster-list-host">${renderCardGrid()}</div>
      <p class="hint gm-monster-toast" id="gm-monster-toast" role="status"></p>
    </div>

    <div id="gm-monster-modal" class="gm-monster-modal" hidden>
      <button type="button" class="gm-monster-modal-backdrop" id="gm-monster-modal-backdrop" aria-label="ปิด"></button>
      <div
        class="gm-monster-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gm-monster-modal-title"
      >
        <header class="gm-monster-modal-head">
          <h2 class="gm-monster-modal-title" id="gm-monster-modal-title">${modalTitle()}</h2>
          <button type="button" class="btn btn-ghost btn-sm gm-monster-modal-close" id="gm-monster-modal-close" aria-label="ปิด">✕</button>
        </header>
        <div class="gm-monster-modal-body" id="gm-monster-modal-body"></div>
      </div>
    </div>
  `;
}

function readForm(root: HTMLElement): FormState | null {
  const name = root.querySelector<HTMLInputElement>("#gm-monster-name")?.value.trim();
  if (!name) return null;
  const num = (id: string) => Math.floor(Number(root.querySelector<HTMLInputElement>(`#${id}`)?.value ?? 0));
  return {
    ...formState,
    id: editingId ?? "",
    name,
    cellsW: num("gm-monster-cells-w"),
    cellsH: num("gm-monster-cells-h"),
    monsterLevel: num("gm-monster-level"),
    monsterBaseHp: num("gm-monster-hp"),
    monsterBaseAttack: num("gm-monster-atk"),
    monsterBaseDefense: num("gm-monster-def"),
    monsterBaseDodge: num("gm-monster-dodge"),
    monsterXpReward: num("gm-monster-xp"),
  };
}

function refreshCardGrid(root: HTMLElement): void {
  const host = root.querySelector<HTMLElement>("#gm-monster-list-host");
  const countEl = root.querySelector<HTMLElement>("#gm-monster-catalog-count");
  if (host) host.innerHTML = renderCardGrid();
  if (countEl) countEl.textContent = `${loadGmMonsterCatalog().length} รายการ`;
}

function setStatus(root: HTMLElement, msg: string): void {
  const el = root.querySelector<HTMLElement>("#gm-monster-status");
  if (el) el.textContent = msg;
}

function setToast(root: HTMLElement, msg: string): void {
  const el = root.querySelector<HTMLElement>("#gm-monster-toast");
  if (el) el.textContent = msg;
}

export function bindGmMonstersSection(root: HTMLElement, userId: string): () => void {
  let stopPreview: (() => void) | undefined;
  let unbindClean: (() => void) | undefined;

  const bindCleanControls = () => {
    unbindClean?.();
    unbindClean = undefined;
    const cleanHost = root.querySelector<HTMLElement>("#gm-monster-upload-clean");
    if (cleanHost) unbindClean = bindSheetUploadCleanControls(cleanHost);
  };

  const mountPreview = () => {
    stopPreview?.();
    stopPreview = undefined;
    const url = previewSheetUrl();
    const host = root.querySelector<HTMLElement>("#gm-monster-sheet-preview-host");
    if (!host || !url) return;
    stopPreview = mountSheetUploadPreview(
      host,
      url,
      draftGridTuning,
      (tuning) => {
        draftGridTuning = tuning;
      },
      {
        sourceDataUrl: draftSheetSourceUrl ?? url,
        onSheetDataUrlChange: (next) => {
          draftSheetUrl = next;
        },
      },
    );
  };

  const remountModalForm = () => {
    const body = root.querySelector<HTMLElement>("#gm-monster-modal-body");
    const title = root.querySelector<HTMLElement>("#gm-monster-modal-title");
    if (title) title.textContent = modalTitle();
    if (!body) return;
    stopPreview?.();
    stopPreview = undefined;
    body.innerHTML = renderForm();
    bindCleanControls();
    mountPreview();
  };

  const closeModal = () => {
    const modal = root.querySelector<HTMLElement>("#gm-monster-modal");
    if (!modal) return;
    modal.hidden = true;
    document.body.classList.remove("gm-monster-modal-open");
    stopPreview?.();
    stopPreview = undefined;
    unbindClean?.();
    unbindClean = undefined;
  };

  const openModal = (monsterId: string | null) => {
    if (monsterId) {
      const m = loadGmMonsterCatalog().find((x) => x.id === monsterId);
      if (!m) return;
      editingId = m.id;
      draftSheetUrl = null;
      draftSheetSourceUrl = null;
      draftGridTuning = m.gridTuning ?? defaultGridTuning();
      formState = { ...m };
    } else {
      formState = formFromDefaults();
    }
    const modal = root.querySelector<HTMLElement>("#gm-monster-modal");
    if (!modal) return;
    remountModalForm();
    modal.hidden = false;
    document.body.classList.add("gm-monster-modal-open");
    root.querySelector<HTMLInputElement>("#gm-monster-name")?.focus();
  };

  void hydrateGmMonsterCatalog().then(() => refreshCardGrid(root));

  const onSubmit = async (event: Event) => {
    if ((event.target as HTMLElement).id !== "gm-monster-form") return;
    event.preventDefault();
    const data = readForm(root);
    if (!data) {
      setStatus(root, "กรอกชื่อมอนสเตอร์");
      return;
    }

    const cleanHost = root.querySelector<HTMLElement>("#gm-monster-upload-clean");
    const cleanPrefs = cleanHost
      ? readSheetUploadCleanPrefsFromHost(cleanHost)
      : getSheetUploadCleanPrefs();
    let sheetForParse = draftSheetUrl ?? data.spritesheetDataUrl;
    if (draftSheetSourceUrl) {
      sheetForParse = await applySheetUploadClean(draftSheetSourceUrl, cleanPrefs);
      draftSheetUrl = sheetForParse;
    }
    let imageDataUrl = data.imageDataUrl || GM_MONSTER_PLACEHOLDER_IMAGE;
    let spritesheetDataUrl = data.spritesheetDataUrl;
    let grid = data.grid;
    if (sheetForParse) {
      try {
        const parsed = await parseCharacterSpritesheet(sheetForParse, draftGridTuning);
        spritesheetDataUrl = parsed.spritesheetDataUrl;
        grid = parsed.grid;
        imageDataUrl = parsed.actions.idle;
      } catch (err) {
        setStatus(
          root,
          err instanceof Error ? err.message : "แยกสไปรต์ชีตไม่สำเร็จ — ปรับกริดแล้วลองใหม่",
        );
        return;
      }
    } else if (!spritesheetDataUrl) {
      setStatus(root, "อัปโหลดสไปรต์ชีต action frame ก่อนบันทึก");
      return;
    }

    const existing = editingId
      ? loadGmMonsterCatalog().find((m) => m.id === editingId)
      : undefined;
    const { id: _omitId, ...rest } = data;
    const entry = createGmMonsterTemplate({
      ...rest,
      imageDataUrl,
      spritesheetDataUrl,
      grid,
      gridTuning: draftGridTuning,
      id: editingId ?? undefined,
      createdAt: existing?.createdAt,
    });
    try {
      await upsertGmMonsterCatalog(entry);
    } catch (err) {
      if (isStorageQuotaError(err)) {
        setStatus(
          root,
          "พื้นที่เบราว์เซอร์ไม่พอ — ลบมอนสเตอร์เก่า หรือลดขนาดสไปรต์ชีตแล้วลองใหม่",
        );
      } else {
        setStatus(root, err instanceof Error ? err.message : "บันทึกไม่สำเร็จ");
      }
      return;
    }
    const wasEdit = Boolean(editingId);
    formState = formFromDefaults();
    editingId = null;
    closeModal();
    refreshCardGrid(root);
    setToast(root, wasEdit ? "บันทึกแล้ว" : "สร้างมอนสเตอร์แล้ว");
  };

  const onClick = async (event: Event) => {
    const target = event.target as HTMLElement;

    if (
      target.id === "gm-monster-open-create" ||
      target.closest("#gm-monster-open-create")
    ) {
      openModal(null);
      return;
    }
    if (
      target.id === "gm-monster-modal-close" ||
      target.closest("#gm-monster-modal-close") ||
      target.id === "gm-monster-modal-backdrop" ||
      target.closest("#gm-monster-modal-backdrop")
    ) {
      closeModal();
      formState = formFromDefaults();
      editingId = null;
      return;
    }

    if (target.id === "gm-monster-download-template" || target.closest("#gm-monster-download-template")) {
      downloadCharacterTemplate();
      return;
    }
    const motionBtn = target.closest<HTMLElement>("[data-gm-motion]");
    if (motionBtn?.dataset.gmMotion) {
      downloadMotionTemplate(motionBtn.dataset.gmMotion as CharacterActionKey);
      return;
    }

    if (target.id === "gm-monster-cancel-edit" || target.closest("#gm-monster-cancel-edit")) {
      closeModal();
      formState = formFromDefaults();
      editingId = null;
      return;
    }
    const editBtn = target.closest<HTMLElement>(".gm-monster-edit");
    if (editBtn?.dataset.id) {
      openModal(editBtn.dataset.id);
      return;
    }
    const delBtn = target.closest<HTMLElement>(".gm-monster-delete");
    if (delBtn?.dataset.id) {
      if (!window.confirm("ลบมอนสเตอร์จากแคตตาล็อก GM?")) return;
      try {
        await deleteGmMonsterFromCatalog(delBtn.dataset.id);
      } catch (err) {
        setToast(
          root,
          isStorageQuotaError(err)
            ? "ลบไม่สำเร็จ — พื้นที่เบราว์เซอร์เต็ม"
            : "ลบไม่สำเร็จ",
        );
        return;
      }
      refreshCardGrid(root);
      setToast(root, "ลบแล้ว");
      return;
    }
    const homeBtn = target.closest<HTMLElement>(".gm-monster-home");
    if (homeBtn?.dataset.id) {
      const m = loadGmMonsterCatalog().find((x) => x.id === homeBtn.dataset.id);
      if (!m) return;
      const home = await loadHomeRoom(userId);
      const part = gmMonsterTemplateToPart(m);
      const idx = home.parts.findIndex((p) => p.id === part.id);
      if (idx >= 0) home.parts[idx] = part;
      else home.parts.push(part);
      await saveHomeRoom(userId, home);
      setToast(root, `ใส่ «${m.name}» ในคลังบ้านแล้ว`);
    }
  };

  const onChange = async (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.id !== "gm-monster-sheet-file") return;
    const input = target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await readImageFileAsDataUrl(file);
      const probe = new Image();
      await new Promise<void>((resolve, reject) => {
        probe.onload = () => resolve();
        probe.onerror = () => reject(new Error("โหลดภาพไม่สำเร็จ"));
        probe.src = dataUrl;
      });
      sheetLayoutFromImage(probe);
      draftSheetSourceUrl = dataUrl;
      const cleanHost = root.querySelector<HTMLElement>("#gm-monster-upload-clean");
      const cleanPrefs = cleanHost
        ? readSheetUploadCleanPrefsFromHost(cleanHost)
        : getSheetUploadCleanPrefs();
      draftSheetUrl = await applySheetUploadClean(dataUrl, cleanPrefs);
      draftGridTuning = gridTuningForImageSize(
        probe.naturalWidth,
        probe.naturalHeight,
        supportsTemplateGridLayout,
      );
      remountModalForm();
      setStatus(root, "อัปโหลดแล้ว — ปรับกริดแล้วกดบันทึก");
    } catch (err) {
      setStatus(
        root,
        err instanceof Error ? err.message : "อัปโหลดไม่สำเร็จ — ใช้เทมเพลตขนาดเทมเพลต",
      );
      input.value = "";
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;
    const modal = root.querySelector<HTMLElement>("#gm-monster-modal");
    if (!modal || modal.hidden) return;
    closeModal();
    formState = formFromDefaults();
    editingId = null;
  };

  root.addEventListener("submit", onSubmit);
  root.addEventListener("click", onClick);
  root.addEventListener("change", onChange);
  document.addEventListener("keydown", onKeyDown);

  return () => {
    document.removeEventListener("keydown", onKeyDown);
    document.body.classList.remove("gm-monster-modal-open");
    unbindClean?.();
    stopPreview?.();
    root.removeEventListener("submit", onSubmit);
    root.removeEventListener("click", onClick);
    root.removeEventListener("change", onChange);
  };
}
